// Modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "app/hooks/hooks";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// Type Definitions
import type { UploadFile } from "antd/lib/upload/interface";
import type { OperationTypes } from "components/organisms/EditRejectPageTemplate/EditRejectPageTemplate";

// Constants & Utils
import { Routes, EDIT_REJECT_PAGE_TEMPLATE as Constants, FILE_UPLOAD_CONSTANT } from "constants/global";
import { validateAmount, validateRejectRetroClaimReason } from "utils/Validations/fieldValidations";
import { getTotalUploadSize } from "utils/getTotalUploadSize";
import { disableFileUpload } from "utils/disableFileUpload";
import { generateZipFile } from "utils/generateZipFile";
import { uploadedFileTypeCheck } from "utils/uploadedFileTypeCheck";
import { getFileList } from "utils/getFileList";

// Redux
import { getLoadingSelector, rejectReimbursement, updateSelectedVoucherGroupCodeMonthlyDeclarations } from "redux/reimbursement/reimbursementSlice";

type ReimbursementFieldErrorsType = { reason?: boolean; amount?: boolean };

type FormDataProps = { reimb_summ_id: number; remarks?: string; amount?: number; uploadedFiles: UploadFile<any>[] };

export type LocationState = {
	state: { reimbSummaryId: number };
	pathname: string;
};

export function useMonthlyReimbursementRejectPage() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();
	const dataLoading = useAppSelector(getLoadingSelector);

	const { state } = location as LocationState;
	const reimbSummaryId = state?.reimbSummaryId ?? "";

	const [inputFields, setInputFields] = useState<{ reason?: string; amount?: number }>({
		reason: "",
		amount: undefined
	});
	const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
	const [fieldErrors, setFieldErrors] = useState<ReimbursementFieldErrorsType>({
		reason: false,
		amount: false
	});

	const validateFields = useCallback(() => {
		const fieldsToValidate = [
			{ key: "reason", validator: validateRejectRetroClaimReason },
			{
				key: "amount",
				validator: (value: string) => {
					return parseFloat(value) === 0.0001 || validateAmount(value);
				}
			}
		];

		// Reset error state before validations
		setFieldErrors({
			reason: false,
			amount: false
		});

		const errors: Array<{ field: string | undefined }> = [];

		fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean }) => {
			const key = field.key as keyof ReimbursementFieldErrorsType;
			const value = inputFields[key];
			if (value) {
				const isValid = field.validator(value.toString());

				if (!isValid) {
					errors.push({ field: field.key });
				}
			}
		});

		if (errors.length) {
			const errs: ReimbursementFieldErrorsType = {};
			errors.forEach((err) => {
				errs[err.field as keyof ReimbursementFieldErrorsType] = true;
			});
			setFieldErrors(errs);
			return;
		}

		return true;
	}, [inputFields, fieldErrors, setFieldErrors]);

	useEffect(() => {
		validateFields();
	}, [inputFields, fileList]);

	// Check all required fields are entered
	const isNextEnabled = useMemo(() => {
		const values = Object.values(inputFields);
		const errors = Object.values(fieldErrors);

		for (let i = 0; i < values?.length; i++) {
			if (!values[i]) {
				return false;
			}
		}

		for (let i = 0; i < errors?.length; i++) {
			if (errors[i] === true) {
				return false;
			}
		}

		if (!fileList?.length) {
			return false;
		}

		return true;
	}, [inputFields, fieldErrors, fileList]);

	const handleFileUpload = useCallback(
		(info: any) => {
			const filelist = getFileList(info);
			setFileList(filelist);
			disableFileUpload(fileList?.length);
		},
		[fileList, setFileList]
	);

	const handleTextAreaChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setInputFields({ ...inputFields, reason: e.target?.value });
		},
		[inputFields, setInputFields]
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (parseFloat(e.target?.value) > 0) {
				setInputFields({ ...inputFields, amount: parseFloat(e.target?.value) });
			} else {
				setInputFields({ ...inputFields, amount: 0.0001 });
			}
		},
		[inputFields, setInputFields]
	);

	const getFormData = async ({ reimb_summ_id, remarks, amount, uploadedFiles }: FormDataProps) => {
		const formData = new FormData();
		formData.append("reimb_summ_id", reimb_summ_id.toString());
		remarks && formData.append("remarks", remarks);
		amount && formData.append("amount", amount === 0.0001 ? "0" : amount.toString());
		formData.append("files", await generateZipFile(uploadedFiles));
		return formData;
	};

	const rejectApiHandler = async (inputs: { reason?: string; amount?: number }, uploadedFiles: UploadFile<any>[]) => {
		const apiPayload = await getFormData({ reimb_summ_id: reimbSummaryId, remarks: inputs.reason, amount: inputs.amount, uploadedFiles: uploadedFiles });
		try {
			const { errors, message } = await dispatch(rejectReimbursement(apiPayload)).unwrap();
			if (!errors?.length && message) {
				CountDownModal({
					timer: 3,
					title: Constants.CLAIM_REJECTED_LABEL,
					subtitle: Constants.CLAIM_REJECTED_SUBLABEL,
					onTimeComplete: goBack,
					okButton: {
						label: Constants.GOT_IT_LABEL,
						onClick: goBack
					}
				});
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: Constants.CANNOT_SEND_REQUEST,
					onTimeComplete: goBack
				});
			}
		}
	};

	const rejectSubmitHandler = useCallback(
		async (inputs: { reason?: string; amount?: number }, uploadedFiles: UploadFile<any>[]) => {
			const totalFileSize: number = await getTotalUploadSize(uploadedFiles);
			const isFileValid = await uploadedFileTypeCheck(FILE_UPLOAD_CONSTANT.REJECT_MONTHLY_REIMBURSMENT_FILETYPE, fileList);

			if (totalFileSize > 5 || !isFileValid) {
				CountDownModal({
					timer: 3,
					title: !isFileValid ? FILE_UPLOAD_CONSTANT.UNSUPPORTED_FILE_TYPE : FILE_UPLOAD_CONSTANT.MAXIMUM_FILE_LIMIT,
					errorIcon: true
				});
			} else {
				rejectApiHandler(inputs, uploadedFiles);
			}
		},
		[getFormData, dispatch, rejectApiHandler, CountDownModal, getTotalUploadSize, uploadedFileTypeCheck]
	);

	const handleSubmit = useCallback(
		(operationType?: OperationTypes) => {
			if (operationType === "reject") {
				rejectSubmitHandler(inputFields, fileList);
			}
		},
		[inputFields, fileList, rejectSubmitHandler]
	);

	const goBack = useCallback(() => {
		dispatch(updateSelectedVoucherGroupCodeMonthlyDeclarations({}));
		navigate(Routes.MONTHLY_DECLARATIONS, { state: { reload: true } });
	}, [navigate, dispatch]);

	return {
		inputFields,
		isNextEnabled,
		goBack,
		handleFileUpload,
		handleTextAreaChange,
		handleInputChange,
		handleSubmit,
		fieldErrors,
		submissionLoading: dataLoading === 1
	};
}
